import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '@interfaces/common/option';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { VaultTranslatedBaseComponent } from '@src/app/shared/components/base/vault-translated-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  OperationSortKey,
  PerOperationItem,
  SortDirection,
  distinctSorted,
  filterOperations,
  hasActiveFilters,
  operationDifference,
  sortOperations,
} from './economy-operations-filter.util';

export type { PerOperationItem } from './economy-operations-filter.util';

/** Colunas ordenáveis, na ordem do cabeçalho. */
interface SortableColumn {
  key: OperationSortKey;
  label: string;
  /** Rótulo do botão de ordenação no celular. */
  shortLabel: string;
}

@Component({
  selector: 'app-economy-operations-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    VaultFormatCurrencyPipe,
    ModalLoanDetailsComponent,
  ],
  templateUrl: './economy-operations-modal.component.html',
  styleUrls: ['./economy-operations-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyOperationsModalComponent extends VaultTranslatedBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @ViewChild('loanDetailsModal') loanDetailsModalRef: ModalLoanDetailsComponent | null = null;

  readonly operations = signal<PerOperationItem[]>([]);
  benchmarkLabel: string = '';
  /** Empresas selecionadas no dashboard, levadas para a tela de histórico (card 3295). */
  companyIds: string[] = [];
  /** Usuário tem a flag dashboard-economy-history: mostra o botão do histórico. */
  canSeeHistory = false;

  // ── Filtros e ordenação (card 3323) ──────────────────────────────────────
  readonly searchQuery = signal('');
  readonly selectedCurrencies = signal<string[]>([]);
  readonly selectedBanks = signal<string[]>([]);
  readonly sortKey = signal<OperationSortKey>('initial');
  readonly sortDirection = signal<SortDirection>('desc');

  readonly sortableColumns: SortableColumn[] = [
    { key: 'initial', label: 'Prevista Inicial', shortLabel: 'Ini.' },
    { key: 'final', label: 'Prevista até Final', shortLabel: 'Final' },
    { key: 'difference', label: 'Diferença', shortLabel: 'Dif.' },
  ];

  /** Só as moedas presentes nas operações. */
  readonly currencyOptions = computed<Option[]>(() =>
    distinctSorted(this.operations().map(op => op.currencyCode)).map(code => ({
      label: code,
      value: code,
    }))
  );

  readonly bankOptions = computed<Option[]>(() =>
    distinctSorted(this.operations().map(op => op.bankName)).map(name => ({
      label: name,
      value: name,
    }))
  );

  readonly isFiltered = computed(() =>
    hasActiveFilters({
      query: this.searchQuery(),
      currencies: this.selectedCurrencies(),
      banks: this.selectedBanks(),
    })
  );

  readonly visibleOperations = computed(() =>
    sortOperations(
      filterOperations(this.operations(), {
        query: this.searchQuery(),
        currencies: this.selectedCurrencies(),
        banks: this.selectedBanks(),
      }),
      this.sortKey(),
      this.sortDirection()
    )
  );

  readonly totalInitial = computed(() =>
    this.visibleOperations().reduce((sum, op) => sum + (op.economiaPrevista ?? 0), 0)
  );

  readonly totalFinal = computed(() =>
    this.visibleOperations().reduce((sum, op) => sum + (op.economiaReal ?? 0), 0)
  );

  readonly difference = operationDifference;

  constructor(
    translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super(translateService);
  }

  buildOperationLabel(op: PerOperationItem): string {
    const parts = [op.loanCode, op.bankName, op.reference].filter(
      (p): p is string => !!p && p.trim().length > 0
    );
    return parts.join(' · ');
  }

  open(
    operations: PerOperationItem[],
    benchmarkLabel: string,
    companyIds: string[] = [],
    canSeeHistory = false
  ): void {
    if (!operations?.length) {
      return;
    }

    this.companyIds = (companyIds ?? []).filter(Boolean);
    this.canSeeHistory = canSeeHistory && this.companyIds.length > 0;

    this.operations.set([...operations]);
    this.benchmarkLabel = benchmarkLabel;
    this.clearFilters();
    this.sortKey.set('initial');
    this.sortDirection.set('desc');
    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Fechar', '', 'modal-economy-operations', false, false);
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.selectedCurrencies.set([]);
    this.selectedBanks.set([]);
  }

  /** Rótulo dos botões de ordenação do celular, com a direção atual (lá o cabeçalho não tem aria-sort). */
  sortAriaLabel(key: OperationSortKey, label: string): string {
    if (this.sortKey() !== key) return `Ordenar por ${label}`;
    const direction = this.sortDirection() === 'asc' ? 'crescente' : 'decrescente';
    return `${label}, ordem ${direction}. Clique para inverter`;
  }

  /** Clique no cabeçalho: mesma coluna inverte; coluna nova começa desc (texto começa asc). */
  toggleSort(key: OperationSortKey): void {
    if (this.sortKey() === key) {
      this.sortDirection.update(direction => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    this.sortKey.set(key);
    this.sortDirection.set(key === 'operation' ? 'asc' : 'desc');
  }

  sortIcon(key: OperationSortKey): string {
    if (this.sortKey() !== key) return 'pi-sort-alt';
    return this.sortDirection() === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down';
  }

  ariaSort(key: OperationSortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey() !== key) return 'none';
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  /** Abre os detalhes da operação na modal embarcada (in-app), sobre a lista de operações. */
  openLoanDetails(loanOperationId: string): void {
    if (!loanOperationId) return;
    this.loanDetailsModalRef?.open(loanOperationId);
  }

  /** Fecha a modal e abre o histórico da economia das empresas selecionadas (card 3295). */
  openHistory(): void {
    if (!this.companyIds.length) return;
    this.dialogRef?.close();
    void this.router.navigate(['/dashboard/dashboard-management/economia/historico'], {
      queryParams: { companyIds: this.companyIds.join(',') },
    });
  }

  isFormValid(): boolean {
    return true;
  }
}
